import * as z from 'zod';
import { SideSchema } from './game-state';
import { HordePromptAnswerSchema, HordeReferenceValueSchema, IdSchema } from './horde-schema-primitives';
import { RecordedTableFactSchema } from './horde-session-battlefield';
import { CorePhaseSchema, ResolutionModeSchema, SpawnSourceSchema } from './horde-session-core';

export const EffectScopeSchema = z.object({
    factionIds: z.array(IdSchema).optional(),
    objectiveIds: z.array(IdSchema).optional(),
    playerIds: z.array(IdSchema).optional(),
    side: SideSchema.optional(),
    tags: z.array(z.string()).optional(),
    unitIds: z.array(IdSchema).optional(),
});
export type EffectScope = z.infer<typeof EffectScopeSchema>;

export const TimingPointSchema = z.object({
    phase: CorePhaseSchema.optional(),
    round: z.number().int().positive(),
    side: SideSchema.optional(),
    step: z.string().optional(),
});
export type TimingPoint = z.infer<typeof TimingPointSchema>;

export const EffectOperationSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('modifyCharacteristic'),
        stat: z.string(),
        target: EffectScopeSchema,
        value: z.number(),
    }),
    z.object({
        kind: z.literal('modifyRoll'),
        operation: z.enum(['add', 'replace', 'reroll', 'dicePool']),
        roll: z.string(),
        value: z.union([z.boolean(), z.number(), z.array(z.number().int())]),
    }),
    z.object({
        keyword: z.string(),
        kind: z.literal('grantKeyword'),
        target: EffectScopeSchema,
    }),
    z.object({
        keyword: z.string(),
        kind: z.literal('grantWeaponKeyword'),
        target: EffectScopeSchema,
    }),
    z.object({
        action: z.string(),
        eligible: z.boolean(),
        kind: z.literal('setEligibility'),
        target: EffectScopeSchema,
    }),
    z.object({
        amount: z.union([z.number(), z.literal('X')]),
        kind: z.literal('adjustResource'),
        playerScope: EffectScopeSchema,
        resource: z.enum(['SP', 'CP']),
    }),
    z.object({
        allowed: z.boolean(),
        kind: z.literal('restrictPurchase'),
        tagOrEntryId: z.string(),
    }),
    z.object({
        kind: z.literal('modifyPurchaseCost'),
        multiplier: z.number().optional(),
        reduction: z.number().optional(),
        scope: EffectScopeSchema,
    }),
    z.object({
        count: z.number().int().positive(),
        deckId: z.string(),
        ignoreActiveLimit: z.boolean().optional(),
        kind: z.literal('drawCards'),
    }),
    z.object({
        cardId: IdSchema,
        deckId: z.string(),
        kind: z.literal('forceCard'),
        timing: TimingPointSchema,
    }),
    z.object({
        kind: z.literal('startSpawnWorkflow'),
        policyId: IdSchema,
        source: SpawnSourceSchema,
    }),
    z.object({
        entityType: z.string(),
        kind: z.literal('createOrRemoveBoardEntity'),
        operation: z.enum(['create', 'remove']),
    }),
    z.object({
        diceOrAmount: z.string(),
        kind: z.literal('damageOrHeal'),
        target: EffectScopeSchema,
    }),
    z.object({
        choiceType: z.string(),
        kind: z.literal('startPrivateChoice'),
        playerScope: EffectScopeSchema,
    }),
    z.object({
        kind: z.literal('requireTableResolution'),
        procedureId: IdSchema,
    }),
]);
export type EffectOperation = z.infer<typeof EffectOperationSchema>;

export const EffectInstanceSchema = z.object({
    duration: z.enum([
        'untilResolved',
        'thisPhase',
        'thisTurn',
        'thisRound',
        'untilMarkerDestroyed',
        'restOfGame',
    ]),
    id: IdSchema,
    operation: EffectOperationSchema,
    resolutionIds: z.array(IdSchema),
    scope: EffectScopeSchema,
    source: z.object({
        id: IdSchema,
        kind: z.enum(['misery', 'secondary', 'secret', 'resupply', 'factionRule', 'fortification']),
    }),
    status: z.enum(['queued', 'active', 'awaitingInput', 'resolved', 'expired', 'cancelled']),
    timing: z.discriminatedUnion('kind', [
        z.object({
            kind: z.literal('immediate'),
        }),
        z.object({
            kind: z.literal('phaseStart'),
            phase: CorePhaseSchema,
            side: SideSchema,
        }),
        z.object({
            kind: z.literal('phaseEnd'),
            phase: CorePhaseSchema,
            side: SideSchema,
        }),
        z.object({
            kind: z.literal('turnEnd'),
            side: SideSchema,
        }),
        z.object({
            kind: z.literal('roundStart'),
            round: z.number().int().positive(),
        }),
        z.object({
            kind: z.literal('roundEnd'),
            round: z.number().int().positive(),
        }),
        z.object({
            eventType: z.string(),
            kind: z.literal('onEvent'),
        }),
    ]),
    visibility: z.union([
        z.literal('public'),
        z.object({
            privateToPlayerId: IdSchema,
        }),
    ]),
});
export type EffectInstance = z.infer<typeof EffectInstanceSchema>;

export const ActionInstanceSchema = z
    .object({
        completesAt: TimingPointSchema,
        id: IdSchema,
        objectiveIds: z.array(IdSchema),
        participatingUnitRefs: z.array(IdSchema),
        restrictionsSnapshot: z.array(RecordedTableFactSchema),
        result: z
            .object({
                completed: z.boolean().optional(),
                reason: z.string().min(1).max(512).optional(),
            })
            .strict()
            .nullable(),
        source: z
            .object({
                cardId: IdSchema,
                kind: z.enum(['secondary', 'secretObjective']),
                ownerPlayerId: IdSchema.optional(),
            })
            .strict(),
        startedAt: TimingPointSchema,
        status: z.enum(['started', 'awaitingCompletionCheck', 'completed', 'failed', 'cancelled']),
        visibility: z.union([
            z.literal('public'),
            z
                .object({
                    privateToPlayerId: IdSchema,
                })
                .strict(),
        ]),
    })
    .strict();
export type ActionInstance = z.infer<typeof ActionInstanceSchema>;

export const PromptSchema = z
    .object({
        answer: HordePromptAnswerSchema.optional(),
        constraints: z.array(
            z
                .object({
                    kind: z.string().min(1).max(128),
                    value: HordeReferenceValueSchema,
                })
                .strict()
        ),
        id: IdSchema,
        instruction: z.string().min(1),
        kind: z.enum(['confirm', 'choice', 'dice', 'number', 'orderedCandidates', 'report']),
        options: z
            .array(
                z
                    .object({
                        id: IdSchema,
                        label: z.string().min(1).max(256),
                        value: HordeReferenceValueSchema,
                    })
                    .strict()
            )
            .optional(),
        resolutionMode: ResolutionModeSchema,
        ruleReference: z.string().min(1),
        status: z.enum(['open', 'answered', 'cancelled']),
        title: z.string().min(1),
        visibility: z.union([
            z.literal('public'),
            z
                .object({
                    privateToPlayerId: IdSchema,
                })
                .strict(),
        ]),
        workflowRevision: z.number().int().nonnegative(),
    })
    .strict();
export type HordePrompt = z.infer<typeof PromptSchema>;

export const ActiveCardSchema = z.object({
    cardId: IdSchema,
    effectIds: z.array(IdSchema),
    revealId: IdSchema,
    revealedRound: z.number().int().positive(),
    status: z.enum(['revealing', 'active', 'resolved', 'cancelled']),
});
export type ActiveCard = z.infer<typeof ActiveCardSchema>;

export const ActiveSecondarySchema = z.object({
    actionIds: z.array(IdSchema),
    cardId: IdSchema,
    effectIds: z.array(IdSchema),
    punishmentApplicationId: IdSchema.nullable(),
    revealedRound: z.number().int().positive(),
    rewardApplicationId: IdSchema.nullable(),
    status: z.enum(['unresolved', 'successful', 'failed']),
    tally: z.record(z.string(), z.number()),
});
export type ActiveSecondary = z.infer<typeof ActiveSecondarySchema>;

export const RevealedSecretObjectiveSchema = z.object({
    cardId: IdSchema,
    outcome: z.enum(['completed', 'failed']).nullable(),
    progress: z.record(z.string(), z.number()),
    revealedRound: z.number().int().positive(),
});
export type RevealedSecretObjective = z.infer<typeof RevealedSecretObjectiveSchema>;

export const PublicCardStateSchema = z.object({
    misery: z.object({
        active: z.array(ActiveCardSchema),
        discardCount: z.number().int().nonnegative(),
        drawCount: z.number().int().nonnegative(),
    }),
    secondary: z.object({
        active: z.array(ActiveSecondarySchema),
        discardCount: z.number().int().nonnegative(),
        drawCount: z.number().int().nonnegative(),
        limitOverride: z.number().int().positive().nullable(),
    }),
    secretObjectives: z.object({
        availableCount: z.number().int().nonnegative(),
        revealedByPlayer: z.record(IdSchema, RevealedSecretObjectiveSchema.nullable()),
    }),
});
export type PublicCardState = z.infer<typeof PublicCardStateSchema>;
